// reporteUnificadoResumen.js
// ————— Estilos mejorados, misma lógica de datos —————

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import Swal from 'sweetalert2';

import { _imagenLogo } from '../../imagenesBase64/logo';
import { fechaActual } from '../../Helpers/fechas';
import { transformarIso8601 } from '../../Helpers/fechas';
import { formatearMontoMoneda } from '../../Helpers/utilidades-globales';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const COLORS = {
  primary: '#000000',  
  accent:  '#0ea5e9',
  gray900: '#111827',
  gray700: '#374151',
  gray600: '#4b5563',
  gray400: '#9ca3af',
  gray300: '#d1d5db',
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  title:   '#000000',  
};

// —————————————————— API pública —————————————————— //
export function imprimir(
  datos,
  nombrePDF,
  tituloPDF,
  _callbackMap,
  _arrayCampos,
  orientacion = 'portrait',
  parametros = {}
) {
  try {
    Swal.fire({
      html: 'Buscando...',
      timer: 600,
      didOpen: () => Swal.showLoading(),
    }).then(() => {
      generarDocumento(
        Array.isArray(datos) ? datos : [],
        nombrePDF || 'reporte.pdf',
        tituloPDF || 'Ingresos integrales',
        orientacion,
        parametros || {}
      );
    });
  } catch (err) {
    const mensaje_html = `<p>Hubo un error al generar el documento</p><p>${err}</p>`;
    Swal.fire({ html: mensaje_html, icon: 'warning', confirmButtonColor: '#3085d6' });
  }
}

// —————————————————— Implementación —————————————————— //
function generarDocumento(datos, nombrePDF, tituloPDF, orientacion = 'portrait', parametros = {}) {
  // 1) Agrupación: Rubro → Año → Mes
  const agrupado = {};
  let totalGeneral = 0;

  datos.forEach((raw) => {
    const item = normalizarItem(raw);
    const rubro = item.rubro || 'Sin Rubro';
    const anio  = item.año || item.anio || item.year || '';
    const mes   = +item.mes || 0;

    if (!agrupado[rubro]) agrupado[rubro] = {};
    if (!agrupado[rubro][anio]) agrupado[rubro][anio] = {};
    if (!agrupado[rubro][anio][mes]) agrupado[rubro][anio][mes] = [];

    agrupado[rubro][anio][mes].push(item);
  });

  // 2) Contenido con estilos mejorados (sin tocar la lógica)
  const contenido = [];
  Object.keys(agrupado).forEach((rubro) => {
    contenido.push(sectionTitle(rubro));

    let totalRubro = 0;

    Object.keys(agrupado[rubro]).forEach((anio) => {
      const meses = Object.keys(agrupado[rubro][anio]).map(Number).sort((a, b) => a - b);

      meses.forEach((mes) => {
        const registros = agrupado[rubro][anio][mes] || [];
        if (!registros.length) return;

        contenido.push(monthTitle(anio, mes));

        let totalMes = 0;

        registros.forEach((it, idx) => {
          totalMes += it.monto_numerico;
          totalRubro += it.monto_numerico;
          totalGeneral += it.monto_numerico;

          //contenido.push(detailBlock(it));
          contenido.push(detailBlock(it, parametros.hiddenCols || []));
          contenido.push(hrDashed());
        });

        // Total del mes
        //contenido.push(totalRow(`TOTAL ${MESES[mes - 1]} ${anio}`, totalMes));
      });
    });

    if (!(parametros.hiddenCols || []).includes('subtotal'))
      contenido.push(totalRow(`SUB-TOTAL ${rubro.toUpperCase()}`, totalRubro, 11, [0, 10, 0, 16])); // Total por rubro
  });

  // Total general
  contenido.push(totalRow('TOTAL GENERAL', totalGeneral, 12, [0, 12, 0, 0]));

  // 3) DocDefinition (header/footer conservados)
  const docDefinition = {
    pageOrientation: orientacion,
    pageMargins: [25, 110, 25, 50],

    header: (currentPage, pageCount, pageSize) => {
      if (currentPage === 1) {
        return [
          {
            columns: [
              { image: _imagenLogo, width: 50, alignment: 'left' },
              { text: 'Unión de las Asambleas de Dios - Ingresos integrales', margin: [20, 0, 0, 0] }
            ],
            margin: [25, 25, 25, 6]
          },
          { text: tituloPDF, alignment: 'left', margin: [25, 0, 25, 8], fontSize: 9, bold: true, color: COLORS.title },
          {
            width: pageSize.width,
            canvas: [
              { type: 'line', x1: 0, y1: 5, x2: pageSize.width - 50, y2: 5, lineWidth: 1, lineColor: 'black' }
            ],
            margin: [25, 0, 25, 0]
          }
        ];
      }
      return [
        { text: tituloPDF, alignment: 'left', margin: [25, 20, 25, 8], fontSize: 9, bold: true, color: COLORS.title },
        {
          width: pageSize.width,
          canvas: [
            { type: 'line', x1: 0, y1: 5, x2: pageSize.width - 50, y2: 5, lineWidth: 1, lineColor: 'black' }
          ],
          margin: [25, 0, 25, 0]
        }
      ];
    },

    footer: (currentPage, pageCount) => ({
      margin: [25, 0, 25, 20],
      table: {
        widths: ['*', '*'],
        body: [[
          { text: fechaActual('DD/MM/YYYY'), alignment: 'left', fontSize: 8 },
          { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', fontSize: 8 }
        ]]
      },
      layout: 'noBorders'
    }),

    content: contenido,

    defaultStyle: {
      fontSize: 9,
      color: COLORS.gray900
    },

    styles: {
      // alias/compatibilidad
      estiloTabla: { fontSize: 8, characterSpacing: -0.3, margin: [0, 2, 0, 0] },
      estilo_tabla: { fontSize: 8, characterSpacing: -0.3, margin: [0, 2, 0, 0] },
    
      sectionTitleText: {
        bold: true,
        fontSize: 11,
        color: COLORS.title,     // << negro puro
      },
      monthBadge: {
        bold: true,
        fontSize: 10,
        color: COLORS.title,     // << negro puro
      },
      totalLabel: {
        bold: true,
        fontSize: 10,
        color: COLORS.gray700,
      },
      totalAmount: {
        bold: true,
        fontSize: 11,
        alignment: 'right',
      },
      amountCell: {
        bold: true,
        alignment: 'right',
        margin: [0, 2, 0, 0],
      },
      metaRow: {
        color: COLORS.gray700,
      },
    },
  };

  if (nombrePDF) pdfMake.createPdf(docDefinition).download(nombrePDF);
  else pdfMake.createPdf(docDefinition).open();
}

// —————————————————— Helpers visuales —————————————————— //
function sectionTitle(text) {
  // Cinta con fondo suave
  return {
    table: {
      widths: ['*'],
      body: [[{ text, style: 'sectionTitleText', fillColor: COLORS.gray100, margin: [8, 6, 8, 6] }]],
    },
    layout: 'noBorders',
    margin: [0, 12, 0, 8],
  };
}

function monthTitle(anio, mes) {
  return {
    table: {
      widths: ['auto'],
      body: [[{ text: `Año ${anio} — ${MESES[Number(mes) - 1]}`, style: 'monthBadge', margin: [8, 4, 8, 4] }]],
    },
    layout: 'noBorders',
    margin: [0, 4, 0, 6],
  };
}

function hrDashed() {
  return {
    canvas: [{
      type: 'line',
      x1: 0, y1: 5, x2: 545, y2: 5,
      lineWidth: 0.5,
      dash: { length: 1, space: 2 },
      lineColor: COLORS.gray300,
    }],
    margin: [0, 2, 0, 2],
  };
}

function totalRow(label, value, fontSize = 10, margin = [0, 6, 0, 10]) {
  return {
    columns: [
      { width: '*', text: label, style: 'totalLabel', fontSize },
      {
        width: 120,
        table: { body: [[{ text: formatearMontoMoneda(value), style: 'totalAmount' }]] },
        layout: 'noBorders',
        margin: [37, 0, 0, 0],
      },
    ],
    margin,
  };
}

function detailBlock(item, hiddenCols = []) {
  const rows = [];

  // Línea principal: "fecha - comprobante - contribuyente" (2 columnas) + monto (3ra)
  const fechaTxt = transformarIso8601(item.fecha) || '';
  const compTxt  = (item.comprobante || '').toString().trim();
  const contTxt  = (item.contribuyente || '').toString().trim();
  const cabecera = [fechaTxt, compTxt, contTxt].filter(Boolean).join(' - ');

  rows.push([
    { text: cabecera, bold: true, margin: [0, 1, 0, 1], colSpan: 2 },
    {},
    { text: formatearMontoMoneda(item.monto_numerico), style: 'amountCell', fillColor: COLORS.gray100 }
  ]);

  // (Se elimina la fila separada de fecha porque ya va en la cabecera)

  // Fondo (solo si NO está oculto)
  if (!hiddenCols.includes('fondo')) {
    rows.push([
      { text: item.detalle || '', colSpan: 2, style: 'metaRow', margin: [0, 0, 0, 0] },
      {},
      { text: '' }
    ]);
  }

  // Forma de pago (solo si NO está oculto)
  if (!hiddenCols.includes('formapago')) {
    rows.push([
      { text: (item.fp_rc || item.fp || item.formapago || ''), colSpan: 2, style: 'metaRow', margin: [0, 0, 0, 0] },
      {},
      { text: '' }
    ]);
  }

  // Título (solo si NO está oculto)
  if (!hiddenCols.includes('titulo')) {
    rows.push([
      { text: `Titular: ${item.titular || ''}`, colSpan: 2, style: 'metaRow', margin: [0, 0, 0, 0] },
      {},
      { text: '' }
    ]);
  }

  return {
    layout: 'noBorders',
    style: 'estiloTabla',
    unbreakable: true,
    table: {
      widths: ['*', '*', 110],
      body: rows,
    },
  };
}

// —————————————————— Helpers de datos —————————————————— //
function normalizarItem(x) {
  // Montos: prioridad monto_distr > monto_fp > monto
  const md  = toNumber(x.monto_distr);
  const fp  = toNumber(x.monto_fp ?? x.monto);
  const pct = toNumber(x.porcentaje);

  let monto_numerico = 0;
  if (!isNaN(md) && md !== 0) {
    monto_numerico = md;
  } else if (!isNaN(fp)) {
    monto_numerico = (!isNaN(pct) && pct !== 0) ? fp * pct : fp;
  }

  return {
    ...x,
    monto_numerico,
  };
}

function toNumber(v) {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(/\./g, '').replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}
